import React, { useContext, useLayoutEffect, useRef } from 'react'
import { Transition, TransitionGroup } from 'react-transition-group'
import PropTypes from 'prop-types'

import { GlobalStyle } from 'styles/global-styles'
import { LayoutWrapper, PageWrapper, ContentWrapper } from './index.styled'

import Header from 'components/header'
import CookiesBanner from 'components/cookies-banner'
import LayoutProvider, { LayoutContext } from 'providers/layout.provider'
import { gsap, ScrollSmoother, ScrollTrigger } from '@dboxdev/ui-components/gsap'

import 'styles/fonts.css'

const LayoutContent = ({ children, location, className }) => {
  return (
    <div className={className}>
      <TransitionGroup component={null}>
        <Transition
          key={location.pathname}
          timeout={{ enter: 1000, exit: 1000 }}
        >
          {(status) => (
            <div className={`page ${status}`}>
              <LayoutWrapper id='layoutWrapper'>
                {children}
              </LayoutWrapper>
            </div>
          )}
        </Transition>
      </TransitionGroup>
    </div>
  )
}

const LayoutGsapWrapper = ({
  children,
  location,
  headerData
}) => {
  const { mobile, scrollSmoother } = useContext(LayoutContext)
  const refWrapper = useRef(null)

  useLayoutEffect(() => {
    // This effect re-creates ScrollSmoother on every navigation. The previous
    // effect's cleanup (revert) has just restored the native scroll to the
    // OUTGOING page's offset, and ScrollSmoother.create adopts whatever that
    // offset is — so the new page would open at the old scroll position and
    // then visibly slide up to the top as the smoother settles. Reset the
    // native scroll to the top first so the new smoother starts there cleanly.
    window.scrollTo(0, 0)

    const gsapContext = gsap.context(() => {
      scrollSmoother.current = ScrollSmoother.create({
        content: '.content',
        wrapper: '.wrapper',
        smooth: 2,
        effects: true,
        normalizeScroll: { allowNestedScroll: true }
      })
    }, [refWrapper])

    // Auto-height sections settle after mount (images/video metadata load),
    // shifting everything below them — pinned ScrollTriggers created earlier
    // keep stale positions. Re-measure whenever the content height changes.
    const content = refWrapper.current?.querySelector('.content')
    const refresh = gsap.delayedCall(0.2, () => ScrollTrigger.refresh()).pause()
    let lastHeight = 0
    const resizeObserver = new window.ResizeObserver(([entry]) => {
      const height = Math.round(entry.contentRect.height)
      if (height === lastHeight) return
      lastHeight = height
      refresh.restart(true)
    })
    if (content) resizeObserver.observe(content)

    return () => {
      resizeObserver.disconnect()
      refresh.kill()
      gsapContext.revert()
    }
  }, [children, mobile, scrollSmoother])

  return (
    <PageWrapper>
      {/* Header lives outside the ScrollSmoother wrapper so position:fixed works. */}
      {headerData && <Header />}
      <ContentWrapper className='wrapper' ref={refWrapper}>
        <LayoutContent location={location} className='content'>
          {children}
        </LayoutContent>
      </ContentWrapper>
      <CookiesBanner />
    </PageWrapper>
  )
}

const Layout = ({
  children,
  pageContext,
  ...props
}) => (
  <LayoutProvider>
    <GlobalStyle />
    <LayoutGsapWrapper {...props} headerData={pageContext?.headerData || null}>
      {children}
    </LayoutGsapWrapper>
  </LayoutProvider>
)

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object.isRequired,
  pageContext: PropTypes.object
}

export default Layout
